import { useEffect, useRef } from "preact/hooks"

import { baixarAnexo, urlDeAnexoSegura } from "../lib/anexo"
import { formatTime } from "../lib/hours"
import type { Entry } from "../lib/types"
import { IconClock, IconDownload, IconExpand, IconTick, IconTicks } from "./icons"
import { Texto } from "./Texto"

type Props = {
  entries: Entry[]
  typing: boolean
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  /**
   * Clique num botao do fluxo.
   *
   * Vai o rotulo (que a pessoa "escreveu") e o botao inteiro: o fluxo casa a
   * condicao pelo ID, entao dois botoes de mesmo nome nao se confundem.
   */
  onOption: (titulo: string, opcao: { id: string; title: string }) => void
  /** Abre a imagem no visor. Fica na janela, não aqui: o visor cobre tudo. */
  onAmpliar: (url: string, mime: string | null) => void
}

/**
 * A lista da conversa.
 *
 * `aria-live="polite"` no container: uma mensagem que chega enquanto a pessoa
 * usa leitor de tela precisa ser anunciada sem roubar o foco de onde ela está.
 */
export function Conversation({
  entries,
  typing,
  hasMore,
  loadingMore,
  onLoadMore,
  onOption,
  onAmpliar,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)

  // Só rola sozinho quando a pessoa já estava no fim. Rolar para o fim enquanto
  // ela lê o histórico é o jeito mais rápido de perder o que ela procurava.
  useEffect(() => {
    const node = ref.current
    if (!node || !stickToBottom.current) return
    node.scrollTop = node.scrollHeight
  }, [entries, typing])

  const onScroll = () => {
    const node = ref.current
    if (!node) return
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight
    stickToBottom.current = distance < 40
  }

  return (
    <div class="list" ref={ref} onScroll={onScroll} aria-live="polite" role="log">
      {hasMore && (
        <button
          type="button"
          class="load-more"
          onClick={onLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? "Carregando…" : "Ver mensagens anteriores"}
        </button>
      )}

      {entries.map((entry) =>
        entry.kind === "system" ? (
          <div class="row sys" key={entry.id}>
            <div class="bubble">
              <Texto texto={entry.text} />
            </div>
          </div>
        ) : (
          <div
            class={`row ${entry.message.direction === "inbound" ? "out" : "in"}`}
            key={entry.message.id}
          >
            <div>
              <div class="bubble">
                <Texto texto={entry.message.text} />
                {entry.message.attachments.map((attachment, index) => {
                  // A URL do anexo vem da MENSAGEM, e o widget roda na pagina da LOJA: confere
                  // o esquema UMA vez e usa o valor conferido nos tres ramos. Ver
                  // `urlDeAnexoSegura` em lib/anexo.
                  const urlSegura = urlDeAnexoSegura(attachment.url)
                  return urlSegura && attachment.type === "image" ? (
                    <figure class="attachment-box" key={`${entry.message.id}-${index}`}>
                      {/* A imagem inteira é o alvo de ampliar: é o gesto que a
                          pessoa já tenta antes de procurar botão. */}
                      <img
                        class="attachment"
                        src={urlSegura}
                        alt="Imagem enviada na conversa"
                        loading="lazy"
                        onClick={() => onAmpliar(urlSegura, attachment.mime)}
                      />
                      <div class="attachment-acoes">
                        <button
                          type="button"
                          class="attachment-acao"
                          onClick={() => onAmpliar(urlSegura, attachment.mime)}
                        >
                          <IconExpand />
                          Ampliar
                        </button>
                        <button
                          type="button"
                          class="attachment-acao"
                          onClick={() => void baixarAnexo(urlSegura, attachment.mime)}
                        >
                          <IconDownload />
                          Baixar
                        </button>
                      </div>
                    </figure>
                  ) : urlSegura && attachment.type === "audio" ? (
                    <div class="attachment-audio-box" key={`${entry.message.id}-${index}`}>
                      {/* `controls` nativo: o navegador ja' resolve play/pausa,
                          tempo e volume — nao ha o que ganhar reimplementando
                          isto num widget que so' precisa tocar um audio. */}
                      <audio class="attachment-audio" controls src={urlSegura} />
                      <div class="attachment-acoes">
                        <button
                          type="button"
                          class="attachment-acao"
                          onClick={() => void baixarAnexo(urlSegura, attachment.mime)}
                        >
                          <IconDownload />
                          Baixar
                        </button>
                      </div>
                    </div>
                  ) : urlSegura ? (
                    <div class="attachment-acoes" key={`${entry.message.id}-${index}`}>
                      <a
                        class="attachment-acao"
                        href={urlSegura}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir ({attachment.type})
                      </a>
                      <button
                        type="button"
                        class="attachment-acao"
                        onClick={() => void baixarAnexo(urlSegura, attachment.mime)}
                      >
                        <IconDownload />
                        Baixar
                      </button>
                    </div>
                  ) : null
                })}
              </div>
              {!!entry.message.options?.length && (
                <div class="options" role="group" aria-label="Opcoes da conversa">
                  {entry.message.options.map((opcao) => (
                    <button
                      type="button"
                      class="option"
                      key={`${entry.message.id}-${opcao.id || opcao.title}`}
                      onClick={() => onOption(opcao.title, opcao)}
                    >
                      {opcao.title}
                    </button>
                  ))}
                </div>
              )}
              <div class="rodape">
                <time class="time">{formatTime(entry.message.timestamp)}</time>
                {entry.message.direction === "inbound" && (
                  // Relogio enquanto o servidor nao confirmou (o eco otimista
                  // nasce com id `local-`); um tique quando entregou; dois
                  // quando o atendente leu.
                  <span
                    class={`visto${entry.message.readAt ? " lido" : ""}`}
                    title={
                      entry.message.readAt
                        ? "Lida pelo atendente"
                        : entry.message.id.startsWith("local-")
                          ? "Enviando"
                          : "Entregue"
                    }
                    aria-label={
                      entry.message.readAt
                        ? "Lida pelo atendente"
                        : entry.message.id.startsWith("local-")
                          ? "Enviando"
                          : "Entregue"
                    }
                  >
                    {entry.message.readAt ? (
                      <IconTicks />
                    ) : entry.message.id.startsWith("local-") ? (
                      <IconClock />
                    ) : (
                      <IconTick />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        ),
      )}

      {typing && (
        <div class="row in">
          <div class="bubble typing" aria-label="Digitando">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
    </div>
  )
}
